/**
 * ICP industry classification constants (TCPR pilot, migration 0057)
 *
 * Governing policy: docs/tcpr-icp-classifier/01_tcpr_icp_classifier_policy.md
 * Structured Output contract: docs/tcpr-icp-classifier/02_tcpr_icp_classifier_schema.json
 *
 * Single source of truth for classification states, industries, exclusion
 * reasons, provenance sources, and queue statuses — consumed by the API
 * classifier service, lead filters, and the web appointments table.
 */

/** Final classification states (exactly one persisted per lead). */
export const ICP_CLASSIFICATION = {
  QUALIFIED: 'QUALIFIED',
  EXCLUDED_ICP: 'EXCLUDED_ICP',
  REVIEW_REQUIRED: 'REVIEW_REQUIRED',
} as const;

export type IcpClassification = typeof ICP_CLASSIFICATION[keyof typeof ICP_CLASSIFICATION];

export const VALID_ICP_CLASSIFICATIONS: IcpClassification[] = Object.values(ICP_CLASSIFICATION);

/** Provenance of the current classification value on a lead row. */
export const ICP_SOURCE = {
  MODEL: 'model', // gpt-5-nano run
  OVERRIDE: 'override', // domain/company-level human ruling
  MANUAL: 'manual', // per-lead human ruling (no generalizable company key)
} as const;

export type IcpSource = typeof ICP_SOURCE[keyof typeof ICP_SOURCE];

/** Queue statuses on interested_leads.icp_status. NULL = not eligible / never queued. */
export const ICP_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

export type IcpStatus = typeof ICP_STATUS[keyof typeof ICP_STATUS];

/**
 * Standard industry labels (policy §"Standard industry labels").
 * Used for the industry filter options and adjudication modal.
 */
export const ICP_INDUSTRIES = [
  'Fashion & Apparel',
  'Technology / SaaS',
  'Consumer Brand',
  'Food & Beverage',
  'Hospitality & Travel',
  'Finance & Fintech',
  'Health & Wellness',
  'Beauty & Personal Care',
  'Premium eCommerce / Retail',
  'Music & Entertainment',
  'Crypto / Digital Assets',
  'Pharma / Supplements / Regulated Health',
  'Other',
] as const;

export type IcpIndustry = (typeof ICP_INDUSTRIES)[number];

/** Standard exclusion reasons (policy §"Standard exclusion reasons"). */
export const ICP_EXCLUSION_REASONS = [
  'Marketing / Creative / PR Agency',
  'Media / Publishing Business',
  'Nonprofit / Charity',
  'Government Organization',
  'Construction',
  'Education',
  'Real Estate Agency',
  'Accounting Firm',
  'Speaker / Event Bureau',
  'Aviation Asset Management',
  'Sports League / Operator',
  'Industry Outside Brief',
] as const;

export type IcpExclusionReason = (typeof ICP_EXCLUSION_REASONS)[number];

/** Policy default-rule threshold: a final decision below this confidence becomes REVIEW_REQUIRED. */
export const ICP_REVIEW_CONFIDENCE_THRESHOLD = 80;

/** Version identifiers persisted on every classification run. */
export const ICP_POLICY_VERSION = 'tcpr-icp-v2';
export const ICP_SCHEMA_VERSION = 'tcpr-icp-schema-v1';
/** policy_version value for human adjudication run rows. */
export const ICP_HUMAN_ADJUDICATION_VERSION = 'human-adjudication';

/**
 * Combined UI filter values for the ICP Status multi-select — maps onto
 * (icp_classification, icp_status, icp_source) combinations server-side.
 */
export const ICP_FILTER_OPTIONS = [
  'QUALIFIED',
  'EXCLUDED_ICP',
  'REVIEW_REQUIRED',
  'PENDING',
  'FAILED',
  'MANUAL_OVERRIDE',
] as const;

export type IcpFilterOption = (typeof ICP_FILTER_OPTIONS)[number];
